import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { Grid, Button } from '@mui/material'
import { CognitoUser } from 'amazon-cognito-identity-js'
import ExternSurface from '../components/ExternSurface'
import { AppContext } from '../context/AppContext'
import {
    AnyInput,
    FeedbackContext,
    useValidatedForm,
} from '@focaltec-suite/commons'
import { confirmPassword } from '@focaltec-suite/api'
import { translate, cognitoErrorTranslator } from '@focaltec-suite/core'

type Mode = 'new' | 'update'

interface ConfirmPasswordFormProps {
    mode: Mode
    cognitoUser?: CognitoUser
    onSuccess?(): any
    onCancel(): any
}

interface ConfirmPasswordRequest {
    password: string
    verification_code: string
}

function ConfirmPasswordForm({
    mode,
    cognitoUser,
    onSuccess,
    onCancel,
}: ConfirmPasswordFormProps) {
    const history = useHistory()
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const { bind, register, isValid } =
        useValidatedForm<ConfirmPasswordRequest>()

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!cognitoUser) {
            return
        }

        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()

        const request = bind()
        confirmPassword(
            cognitoUser,
            request.verification_code,
            request.password
        )
            .then((response) => {
                feedbackContext.showSuccess(
                    translate('confirm.success') as string
                )
                if (onSuccess) {
                    onSuccess()
                } else {
                    history.push(context.fixPath('/'))
                }
            })
            .catch((error) =>
                cognitoErrorTranslator(error, feedbackContext.showError)
            )
            .finally(feedbackContext.closeBackdrop)
    }

    const onClickedCancel = () => {
        feedbackContext.showInfo(
            translate(`confirm.${mode}.cancelled`) as string
        )
        onCancel()
    }

    return (
        <ExternSurface
            title={translate(`confirm.${mode}.title`) as string}
            description={translate(`confirm.${mode}.description`) as string}
        >
            <form autoComplete="off" noValidate onSubmit={onSubmit}>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid item xs={12}>
                        <AnyInput
                            type="text"
                            name="verification_code"
                            label={translate('verification_code') as string}
                            required
                            hiddenLabel
                            register={register}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AnyInput
                            type="password"
                            name="password"
                            label={translate('password') as string}
                            required
                            hiddenLabel
                            register={register}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ my: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                        >
                            {translate('confirm.button')}
                        </Button>
                    </Grid>
                    <Grid item xs="auto">
                        <Button
                            variant="text"
                            color="primary"
                            size="large"
                            onClick={onClickedCancel}
                        >
                            {translate('cancel')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </ExternSurface>
    )
}

export default ConfirmPasswordForm
